import { useState } from 'react'
import { ChevronDown, ChevronUp, MessageSquare, Timer, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import { cn, formatCompactNumber } from '@/lib/utils'
import type { ContextMessage, ToolCallDetail } from '@/types/contextObservability'
import { Badge } from '@/components/ui/badge'

export interface MessagesListProps {
  messages: ContextMessage[]
  toolCalls?: ToolCallDetail[]
  summaryContent?: string | null
  className?: string
}

const roleBadgeVariants: Record<string, { label: string; className: string }> = {
  user: { label: 'USER', className: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-transparent' },
  assistant: { label: 'ASST', className: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-transparent' },
  tool: { label: 'TOOL', className: 'bg-green-500/10 text-green-700 dark:text-green-300 border-transparent' },
  summary: { label: 'SUM', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-transparent' },
}

const kindBadgeVariants: Record<string, { label: string; variant: 'secondary' | 'outline' | 'warning' }> = {
  kept: { label: 'Kept', variant: 'secondary' },
  'kept-first': { label: 'First-Kept', variant: 'secondary' },
  summarized: { label: 'Summarized', variant: 'warning' },
  tool: { label: 'Tool', variant: 'outline' },
  current: { label: 'Current', variant: 'secondary' },
}

type TimelineEntry =
  | { type: 'message'; message: ContextMessage; idx: number }
  | { type: 'toolCall'; toolCall: ToolCallDetail }

function buildTimeline(messages: ContextMessage[], toolCalls?: ToolCallDetail[]): TimelineEntry[] {
  const entries: TimelineEntry[] = messages.map((message, idx) => ({
    type: 'message' as const,
    message,
    idx,
  }))

  if (!toolCalls || toolCalls.length === 0) return entries

  let lastAssistantIdx = -1
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i]
    if (e.type === 'message' && e.message.role === 'assistant') {
      lastAssistantIdx = i
      break
    }
  }

  const toolEntries: TimelineEntry[] = toolCalls.map(tc => ({
    type: 'toolCall' as const,
    toolCall: tc,
  }))

  if (lastAssistantIdx >= 0) {
    entries.splice(lastAssistantIdx + 1, 0, ...toolEntries)
  } else {
    entries.push(...toolEntries)
  }

  return entries
}

export function MessagesList({ messages, toolCalls, summaryContent, className }: MessagesListProps) {
  const [showSummarized, setShowSummarized] = useState(false)
  const [summaryExpanded, setSummaryExpanded] = useState(false)

  const summarizedMessages = messages.filter(m => m.kind === 'summarized')
  const visibleMessages = showSummarized
    ? messages
    : messages.filter(m => m.kind !== 'summarized')

  const totalTokens = messages.reduce((sum, m) => sum + m.tokens, 0)
  const hasSummarized = summarizedMessages.length > 0

  const timeline = buildTimeline(visibleMessages, toolCalls)

  return (
    <div className={cn('space-y-1 overflow-hidden', className)}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {hasSummarized && (
            <button
              onClick={() => setShowSummarized(!showSummarized)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  showSummarized ? 'bg-amber-500' : 'bg-muted-foreground'
                )}
              />
              Show summarized ({summarizedMessages.length})
            </button>
          )}
        </div>
        <span className="text-xs text-muted-foreground font-mono">
          {formatCompactNumber(totalTokens)} total · {messages.length} msg{toolCalls?.length ? ` · ${toolCalls.length} tool call${toolCalls.length !== 1 ? 's' : ''}` : ''}
        </span>
      </div>

      {hasSummarized && !showSummarized && (
        <SummaryBlock
          count={summarizedMessages.length}
          tokens={summarizedMessages.reduce((s, m) => s + m.tokens, 0)}
          content={summaryContent}
          expanded={summaryExpanded}
          onToggle={() => setSummaryExpanded(!summaryExpanded)}
        />
      )}

      {timeline.map((entry, i) => {
        if (entry.type === 'message') {
          return (
            <MessageRow
              key={`msg-${entry.message.role}-${entry.message.timestamp}-${entry.idx}`}
              message={entry.message}
            />
          )
        }
        return (
          <ToolCallRow
            key={`tc-${entry.toolCall.toolCallId}-${i}`}
            toolCall={entry.toolCall}
          />
        )
      })}
    </div>
  )
}

function SummaryBlock({
  count,
  tokens,
  content,
  expanded,
  onToggle,
}: {
  count: number
  tokens: number
  content?: string | null
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 mb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-transparent text-[10px]">
            SUM
          </Badge>
          <span className="text-sm">
            Summarized {count} earlier messages
          </span>
          {content && (
            <button
              onClick={onToggle}
              className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Hide summary
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Show summary
                </>
              )}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono">
            {formatCompactNumber(tokens)} tok
          </span>
          <Badge variant="warning" className="text-[10px] px-1.5 py-0">Summarized</Badge>
        </div>
      </div>

      {expanded && content && (
        <div className="mt-2 border-t border-amber-500/20 pt-2">
          <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-mono bg-muted p-3 rounded-md max-h-[60vh] overflow-y-auto">
            {content}
          </pre>
        </div>
      )}
    </div>
  )
}

function MessageRow({ message }: { message: ContextMessage }) {
  const [expanded, setExpanded] = useState(false)
  const roleMeta = roleBadgeVariants[message.role] ?? {
    label: message.role.toUpperCase(),
    className: 'bg-muted text-muted-foreground border-transparent',
  }
  const kindMeta = kindBadgeVariants[message.kind]
  const hasContent = (message.text?.length ?? 0) > 0

  return (
    <div
      className={cn(
        'rounded-md px-3 py-2 hover:bg-muted/50 transition-colors overflow-hidden',
        !message.included && 'opacity-50'
      )}
    >
      <button
        onClick={() => hasContent && setExpanded(!expanded)}
        className={cn('flex items-center gap-2 w-full text-left min-w-0', hasContent && 'cursor-pointer')}
      >
        <Badge className={cn('text-[10px] px-1.5 py-0 flex-shrink-0', roleMeta.className)}>
          {roleMeta.label}
        </Badge>

        <div className="flex-1 min-w-0 overflow-hidden">
          {message.toolName ? (
            <span className="font-mono text-xs truncate block">{message.toolName}()</span>
          ) : message.text ? (
            <span className="text-sm text-foreground/80 truncate block">
              {message.text}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MessageSquare className="h-3 w-3" /> (no preview)
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-muted-foreground font-mono">
            {formatCompactNumber(message.tokens)} tok
          </span>
          {kindMeta && (
            <Badge variant={kindMeta.variant} className="text-[10px] px-1.5 py-0">
              {kindMeta.label}
            </Badge>
          )}
          {hasContent && (
            expanded
              ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
              : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && message.text && (
        <div className="mt-2 ml-[52px]">
          <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-muted p-3 rounded-md max-h-[60vh] overflow-y-auto">
            {message.text}
          </pre>
        </div>
      )}
    </div>
  )
}

function ToolCallRow({ toolCall }: { toolCall: ToolCallDetail }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-md border border-green-500/20 px-3 py-2 bg-green-500/5 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left min-w-0"
      >
        <Badge className="bg-green-500/10 text-green-700 dark:text-green-300 border-transparent text-[10px] px-1.5 py-0 flex-shrink-0">
          CALL
        </Badge>
        <span className="font-mono text-xs font-medium flex-1 min-w-0 truncate">
          {toolCall.toolName}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Timer className="h-3 w-3" />
            {toolCall.durationMs}ms
          </span>
          <span className="font-mono">
            {toolCall.outputSizeBytes > 1024
              ? `${(toolCall.outputSizeBytes / 1024).toFixed(1)}KB`
              : `${toolCall.outputSizeBytes}B`}
          </span>
          {toolCall.wasCompressed && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">compressed</Badge>
          )}
          {toolCall.isKnowledgeTool && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 border-sky-500/40 text-sky-600">KB</Badge>
          )}
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 ml-[52px]">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <ArrowUpFromLine className="h-3 w-3" /> Input
            </div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-muted p-2 rounded-md max-h-[40vh] overflow-y-auto">
              {toolCall.inputSummary || '(empty)'}
            </pre>
          </div>
          {toolCall.outputPreview && (
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                <ArrowDownToLine className="h-3 w-3" /> Output
              </div>
              <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-muted p-2 rounded-md max-h-[40vh] overflow-y-auto">
                {toolCall.outputPreview}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
